import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { ChunkType } from '@/lib/mastra/types';
import type { AgentMessageInput, AgentStreamOptions } from '@/types/agent';
import type { AbortableMastraClient } from './use-mastra-client';

export const STREAMING_IN_PROGRESS_ERROR = 'STREAMING_IN_PROGRESS';

export type MastraStreamConcurrency = 'reject' | 'replace';
export type MastraStreamUntilIdle = NonNullable<AgentStreamOptions['untilIdle']>;

export interface MastraStreamRequestContext {
  requestId: string;
  signal: AbortSignal;
  isRequestActive: () => boolean;
}

interface RunMastraStreamOptions {
  agentId: string;
  input: AgentMessageInput;
  streamOptions?: AgentStreamOptions;
  untilIdle?: MastraStreamUntilIdle;
  concurrency?: MastraStreamConcurrency;
  onChunk: (chunk: ChunkType, context: MastraStreamRequestContext) => Promise<void> | void;
}

interface UseMastraStreamRunnerReturn {
  isStreaming: boolean;
  runStream: (options: RunMastraStreamOptions) => Promise<void>;
  stop: () => void;
}

type StreamableAgent = ReturnType<AbortableMastraClient['getAgent']>;

async function openAgentStream({
  agent,
  input,
  streamOptions,
  untilIdle,
}: {
  agent: StreamableAgent;
  input: AgentMessageInput;
  streamOptions?: AgentStreamOptions;
  untilIdle?: MastraStreamUntilIdle;
}) {
  return agent.stream(input, {
    ...(streamOptions ?? {}),
    ...(untilIdle ? { untilIdle } : {}),
  });
}

export function useMastraStreamRunner(
  mastraClient: AbortableMastraClient,
): UseMastraStreamRunnerReturn {
  const [isStreaming, setIsStreaming] = useState(false);
  const activeRequestIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    activeRequestIdRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
  }, []);

  const runStream = useCallback(
    async ({
      agentId,
      input,
      streamOptions,
      untilIdle,
      concurrency = 'reject',
      onChunk,
    }: RunMastraStreamOptions): Promise<void> => {
      const hasActiveRequest = activeRequestIdRef.current !== null;

      if (hasActiveRequest && concurrency === 'reject') {
        throw new Error(STREAMING_IN_PROGRESS_ERROR);
      }

      if (hasActiveRequest && concurrency === 'replace') {
        stop();
      }

      const requestId = uuidv4();
      const abortController = new AbortController();
      activeRequestIdRef.current = requestId;
      abortControllerRef.current = abortController;
      setIsStreaming(true);

      const context: MastraStreamRequestContext = {
        requestId,
        signal: abortController.signal,
        isRequestActive: () =>
          activeRequestIdRef.current === requestId && !abortController.signal.aborted,
      };

      try {
        const requestMastraClient = mastraClient.withAbortSignal(abortController.signal);
        const agent = requestMastraClient.getAgent(agentId);
        const stream = await openAgentStream({
          agent,
          input,
          streamOptions,
          untilIdle,
        });

        await stream.processDataStream({
          onChunk: async (chunk) => {
            if (!context.isRequestActive()) {
              return;
            }

            await onChunk(chunk, context);
          },
        });
      } finally {
        if (activeRequestIdRef.current === requestId) {
          activeRequestIdRef.current = null;
          setIsStreaming(false);
        }

        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [mastraClient, stop],
  );

  return {
    isStreaming,
    runStream,
    stop,
  };
}
